import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaHandHoldingHeart,
  FaMoneyBillWave,
  FaChalkboardTeacher,
  FaBook,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { donationAPI } from "../services/postgresService";
import { useSettings } from "../hooks/useSettings";

const donationOptions = [
  {
    id: 1,
    amount: 25,
    titleKey: "donate.basicSupporter",
  },
  {
    id: 2,
    amount: 50,
    titleKey: "donate.silverSupporter",
  },
  {
    id: 3,
    amount: 100,
    titleKey: "donate.goldSupporter",
  },
  {
    id: 4,
    amount: 250,
    titleKey: "donate.platinumSupporter",
  },
];

const DonateSection = ({ onSubmitDonation }) => {
  const { theme } = useSettings();
  const { t } = useTranslation();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [notification, setNotification] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    // Log the donate background image for debugging
    // console.log('🎨 Donate background image:', theme.coverImages?.donate);
  }, [theme.coverImages?.donate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = selectedAmount === "custom"
      ? parseFloat(customAmount || "0")
      : parseFloat(selectedAmount || "0");
    
    if (!amount || amount <= 0) {
      setNotification({ 
        type: "error", 
        message: t("donate.enterValidAmount") || "Please enter a valid amount." 
      });
      return;
    }

    setSubmitting(true);
    setNotification(null);
    
    try {
      // Use PostgreSQL donationAPI to save donation
      await donationAPI.create({
        amount: amount,
        donorName: isAnonymous ? 'Anonymous' : donorName,
        email: isAnonymous ? 'anonymous@example.com' : email,
        message: message || 'No message provided',
        isAnonymous: isAnonymous,
        isRecurring: isRecurring,
      });

      setSubmitted(true);
      setNotification({
        type: "success",
        message: t("donate.thankYou"),
        showContact: true
      });
      
      // Clear form
      setSelectedAmount(null);
      setCustomAmount("");
      setDonorName("");
      setEmail("");
      setMessage("");
      setIsAnonymous(false);
      setIsRecurring(false);
    } catch (error) {
      console.error("Error processing donation:", error);
      setNotification({
        type: "error",
        message: t("donate.error") || "Failed to process donation. Please try again.",
      });
    } finally {
      setSubmitting(false);
      if (!submitted) {
        setTimeout(() => setNotification(null), 4000);
      }
    }
  };

  const donateBackgroundUrl = theme.coverImages?.donate ;
  
  if (submitted) {
    return (
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <div className="text-green-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {t("donate.thankYouTitle") || "Thank You for Your Donation!"}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
              {t("donate.contactSoon") || "We will contact you shortly with payment instructions."}
            </p>
            <div className="bg-gray-100 dark:bg-gray-700 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                {t("donate.contactInfo") || "Contact Information"}
              </h3>
              <div className="space-y-4 text-left max-w-md mx-auto">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                  </svg>
                  <a href="tel:+93782818883" className="text-blue-600 dark:text-blue-400 hover:underline">+93 78 281 8883</a>
                </div>
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                  </svg>
                  <a href="mailto:safir-elite@gmail.com" className="text-blue-600 dark:text-blue-400 hover:underline">safir-elite@gmail.com</a>
                </div>
                <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-600">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("donate.officeHours") || "Our office hours are 9:00 AM - 5:00 PM, Saturday to Thursday"}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setSubmitted(false)}
              className="mt-8 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {t("donate.donateAgain") || "Make Another Donation"}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
      <div
          className="py-24 relative overflow-hidden"
          id="donate"
          style={{
              backgroundImage: `linear-gradient(135deg, rgba(31, 125, 83, 0.15) 0%, rgba(37, 95, 56, 0.15) 100%), url('${donateBackgroundUrl}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundAttachment: "fixed",
              backgroundColor: "#18230F",
          }}
      >
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/40 to-black/50"></div>

          <div className="container mx-auto px-4 relative z-10">
              <motion.div
                  className="text-center mb-16"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
              >
                  <motion.div
                      initial={{ y: -20, opacity: 0 }}
                      whileInView={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.5 }}
                      viewport={{ once: true }}
                  >
                      <FaHandHoldingHeart
                          className="text-safir-primary mx-auto mb-4"
                          size={36}
                      />
                  </motion.div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                      {t("donate.title")}
                  </h2>
                  <motion.div
                      className="w-24 h-1 bg-safir-primary mx-auto mb-6"
                      initial={{ width: 0 }}
                      whileInView={{ width: 96 }}
                      transition={{ duration: 0.8 }}
                      viewport={{ once: true }}
                  ></motion.div>
                  <p className="max-w-2xl mx-auto text-gray-100">
                      {t("donate.description")}
                  </p>
              </motion.div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                  {/* Left side - Donation options */}
                  <motion.div
                      initial={{ opacity: 0, x: -50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                      viewport={{ once: true }}
                  >
                      <div className="bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-xl">
                          <h3 className="text-2xl font-semibold text-white mb-6">
                              {t("donate.howHelps")}
                          </h3>

                          <div className="space-y-6">
                              <div className="flex items-start">
                                  <div className="bg-safir-primary p-3 rounded-full mr-4 ml-4">
                                      <FaBook
                                          className="text-white"
                                          size={20}
                                      />
                                  </div>
                                  <div>
                                      <h4 className="text-xl font-medium text-white">
                                          {t("donate.educationalMaterials")}
                                      </h4>
                                      <p className="text-dark-300 mt-2">
                                          {t("donate.educationalMaterialsDesc")}
                                      </p>
                                  </div>
                              </div>

                              <div className="flex items-start">
                                  <div className="bg-safir-primary p-3 rounded-full mr-4 ml-4">
                                      <FaChalkboardTeacher
                                          className="text-white"
                                          size={20}
                                      />
                                  </div>
                                  <div>
                                      <h4 className="text-xl font-medium text-white">
                                          {t("donate.scholarships")}
                                      </h4>
                                      <p className="text-dark-300 mt-2">
                                          {t("donate.scholarshipsDesc")}
                                      </p>
                                  </div>
                              </div>

                              <div className="flex items-start">
                                  <div className="bg-safir-primary p-3 rounded-full mr-4 ml-4">
                                      <FaMoneyBillWave
                                          className="text-white"
                                          size={20}
                                      />
                                  </div>
                                  <div>
                                      <h4 className="text-xl font-medium text-white">
                                          {t("donate.facilityImprovements")}
                                      </h4>
                                      <p className="text-dark-300  mt-2">
                                          {t("donate.facilityImprovementsDesc")}
                                      </p>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </motion.div>

                  {/* Right side - Donation form */}
                  <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                      viewport={{ once: true }}
                  >
                      <div className="bg-white p-8 rounded-xl shadow-xl">
                          <h3 className="text-2xl font-semibold text-safir-dark mb-6">
                              {t("donate.makeYourDonation")}
                          </h3>

                          <form onSubmit={handleSubmit}>
                              {/* Donation amounts */}
                              <div className="mb-6">
                                  <label className="block text-gray-700 mb-3">
                                      {t("donate.selectAmount")}
                                  </label>
                                  <div className="grid grid-cols-2 gap-4">
                                      {donationOptions.map((option) => (
                                          <motion.button
                                              key={option.id}
                                              type="button"
                                              className={`p-4 border rounded-lg text-center transition-colors ${
                                                  selectedAmount ===
                                                  option.amount
                                                      ? "bg-safir-primary text-white border-safir-primary"
                                                      : "bg-white text-gray-800 border-gray-200 hover:border-safir-primary"
                                              }`}
                                              onClick={() => {
                                                  setSelectedAmount(
                                                      option.amount,
                                                  );
                                                  setCustomAmount("");
                                              }}
                                              whileHover={{
                                                  scale: 1.05,
                                                  boxShadow:
                                                      "0 4px 12px rgba(0,0,0,0.1)",
                                              }}
                                              transition={{
                                                  type: "spring",
                                                  stiffness: 400,
                                                  damping: 10,
                                              }}
                                          >
                                              <div className="text-xl font-bold">
                                                  ${option.amount}
                                              </div>
                                              <div className="text-sm mt-1">
                                                  {t(option.titleKey)}
                                              </div>
                                          </motion.button>
                                      ))}
                                  </div>
                              </div>

                              {/* Custom amount */}
                              <div className="mb-6">
                                  <div className="flex items-center">
                                      <input
                                          type="checkbox"
                                          id="customAmount"
                                          className="mr-2"
                                          checked={selectedAmount === "custom"}
                                          onChange={() =>
                                              setSelectedAmount("custom")
                                          }
                                      />
                                      <label
                                          htmlFor="customAmount"
                                          className="text-gray-700"
                                      >
                                          {t("donate.customAmount")}
                                      </label>
                                  </div>

                                  {selectedAmount === "custom" && (
                                      <div className="mt-3">
                                          <div className="relative">
                                              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                  <span className="text-gray-500">
                                                      $
                                                  </span>
                                              </div>
                                              <input
                                                  type="number"
                                                  className="block w-full pl-8 pr-4 py-2 border border-gray-300 rounded-md focus:ring-safir-primary focus:border-safir-primary"
                                                  placeholder={t(
                                                      "donate.enterAmount",
                                                  )}
                                                  value={customAmount}
                                                  onChange={(e) =>
                                                      setCustomAmount(
                                                          e.target.value,
                                                      )
                                                  }
                                                  min="1"
                                              />
                                          </div>
                                      </div>
                                  )}
                              </div>

                              {/* Donor information */}
                              <div className="mb-4">
                                  <label className="block text-gray-700 mb-2">
                                      {t("donate.name")}
                                  </label>
                                  <motion.input
                                      whileFocus={{ scale: 1.01 }}
                                      type="text"
                                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-safir-primary focus:border-safir-primary"
                                      value={donorName}
                                      onChange={(e) =>
                                          setDonorName(e.target.value)
                                      }
                                      required
                                  />
                              </div>

                              <div className="mb-4">
                                  <label className="block text-gray-700 mb-2">
                                      {t("donate.emailAddress")}
                                  </label>
                                  <motion.input
                                      whileFocus={{ scale: 1.01 }}
                                      type="email"
                                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-safir-primary focus:border-safir-primary"
                                      value={email}
                                      onChange={(e) => setEmail(e.target.value)}
                                      required
                                  />
                              </div>

                              <div className="mb-6">
                                  <label className="block text-gray-700 mb-2">
                                      {t("donate.message")}
                                  </label>
                                  <motion.textarea
                                      whileFocus={{ scale: 1.01 }}
                                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-safir-primary focus:border-safir-primary"
                                      rows="3"
                                      value={message}
                                      onChange={(e) =>
                                          setMessage(e.target.value)
                                      }
                                  ></motion.textarea>
                              </div>

                              <div className="mb-6 space-y-3">
                                  <div className="flex items-center">
                                      <input
                                          type="checkbox"
                                          id="recurringDonation"
                                          className="mr-2 h-4 w-4 text-safir-primary focus:ring-safir-primary"
                                          checked={isRecurring}
                                          onChange={(e) =>
                                              setIsRecurring(e.target.checked)
                                          }
                                      />
                                      <label
                                          htmlFor="recurringDonation"
                                          className="text-gray-700"
                                      >
                                          {t("donate.recurring")}
                                      </label>
                                  </div>
                              </div>

                              <motion.button
                                  whileHover={{
                                      scale: 1.03,
                                      backgroundColor: "#255F38",
                                  }}
                                  whileTap={{ scale: 0.98 }}
                                  type="submit"
                                  className="w-full bg-safir-primary hover:bg-safir-secondary text-white font-semibold py-3 px-4 rounded-md transition-colors duration-300"
                                  disabled={
                                      !selectedAmount ||
                                      (selectedAmount === "custom" &&
                                          !customAmount) ||
                                      submitting
                                  }
                              >
                                  {submitting
                                      ? t("donate.processing")
                                      : t("donate.completeDonation")}
                              </motion.button>
                          </form>
                      </div>
                  </motion.div>
              </div>

              {notification && (
                  <div
                      className={`mb-6 text-center px-4 py-3 rounded-md font-semibold ${
                          notification.type === "success"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-700"
                      }`}
                  >
                      {notification.message}
                  </div>
              )}
          </div>
      </div>
  );
};

export default DonateSection;
